import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, setToken, setRole, API_BASE, getToken } from '../lib/api';
import { BRAND } from '../mock/mock';
import { useToast } from '../hooks/use-toast';
import SendAgreementModal from '../components/admin/SendAgreementModal';
import ApplicationDrawer from '../components/admin/ApplicationDrawer';
import CollegesTab from '../components/admin/CollegesTab';
import StudentsTab from '../components/admin/StudentsTab';
import ForwardsTab from '../components/admin/ForwardsTab';
import MessagesTab from '../components/admin/MessagesTab';
import CommissionsTab from '../components/admin/CommissionsTab';
import DomesticAppsTab from '../components/admin/DomesticAppsTab';
import StudentAidTab from '../components/admin/StudentAidTab';
import CreateRecruiterModal from '../components/admin/CreateRecruiterModal';
import TrashTab from '../components/admin/TrashTab';
import ChangePasswordModal from '../components/common/ChangePasswordModal';
import { useSelection, SelectionCheckbox, BulkActionBar } from '../components/admin/BulkSelect';
import {
  LogOut, Users, FileText, MailOpen, DollarSign, Building2, Send, MessageSquare,
  Mailbox, LayoutDashboard, RefreshCw, Search, CheckCircle2, XCircle, Clock,
  Eye, Trash2, Mail, KeyRound, ShieldAlert, Edit3, Check, X as CloseIcon, RotateCcw, Plus, Menu, GraduationCap, ClipboardList,
} from 'lucide-react';

const NAV = [
  { key: 'recruiters', label: 'Recruiters', icon: Users },
  { key: 'students', label: 'Students', icon: FileText },
  { key: 'direct', label: 'Direct Leads', icon: MailOpen },
  { key: 'domestic', label: 'Domestic Applications', icon: GraduationCap },
  { key: 'student-aid', label: 'Student Aid', icon: ClipboardList },
  { key: 'commissions', label: 'Commissions', icon: DollarSign },
  { key: 'colleges', label: 'Colleges', icon: Building2 },
  { key: 'forwards', label: 'Forward History', icon: Send },
  { key: 'messages', label: 'Messages', icon: MessageSquare },
  { key: 'trash', label: 'Trash', icon: Trash2 },
  { key: 'contact', label: 'Contact Forms', icon: Mailbox },
];

const StatusPill = ({ value }) => {
  const map = {
    pending: { bg: 'bg-[#FEF4DB]', tx: 'text-[#8a5e00]', icon: Clock, label: 'Pending' },
    agreement_sent: { bg: 'bg-[#DEE9FA]', tx: 'text-[#1e40af]', icon: MailOpen, label: 'Agreement Sent' },
    agreement_signed: { bg: 'bg-[#E1F3E4]', tx: 'text-[#0B5E2A]', icon: CheckCircle2, label: 'Signed' },
    agreement_declined: { bg: 'bg-[#FDE2E2]', tx: 'text-[#9b1c1c]', icon: XCircle, label: 'Declined' },
    active: { bg: 'bg-[#E1F3E4]', tx: 'text-[#0B5E2A]', icon: CheckCircle2, label: 'Active' },
    internal: { bg: 'bg-[#EADEFA]', tx: 'text-[#6b21a8]', icon: CheckCircle2, label: 'Internal' },
    approved: { bg: 'bg-[#E1F3E4]', tx: 'text-[#0B5E2A]', icon: CheckCircle2, label: 'Approved' },
    rejected: { bg: 'bg-[#FDE2E2]', tx: 'text-[#9b1c1c]', icon: XCircle, label: 'Rejected' },
  };
  const s = map[value] || { bg: 'bg-[#eee]', tx: 'text-[#333]', icon: Clock, label: value || 'Unknown' };
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${s.bg} ${s.tx} whitespace-nowrap`}>
      <Icon className="h-3.5 w-3.5" /> {s.label}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, accent = 'green' }) => {
  const colors = {
    green: 'bg-[#E1F3E4] text-[#0B5E2A]',
    gold: 'bg-[#FEF4DB] text-[#8a5e00]',
    blue: 'bg-[#DEE9FA] text-[#1e40af]',
    purple: 'bg-[#EADEFA] text-[#6b21a8]',
    rose: 'bg-[#FDE2E2] text-[#9b1c1c]',
  };
  return (
    <div className="bg-white border border-[#0B5E2A]/10 rounded-xl p-5">
      <div className="flex items-center gap-2.5">
        <div className={`h-8 w-8 rounded-md flex items-center justify-center ${colors[accent]}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="text-sm font-semibold text-[#4b5e4d]">{label}</div>
      </div>
      <div className="mt-3 font-display text-3xl font-bold text-[#1b2a1f]">{value ?? '—'}</div>
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tab, setTab] = useState('recruiters');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [recs, setRecs] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [dsStatus, setDsStatus] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [agreeRec, setAgreeRec] = useState(null);
  const [editingCommId, setEditingCommId] = useState(null);
  const [commDraft, setCommDraft] = useState('');
  const [showChangePw, setShowChangePw] = useState(false);
  const [unreadMsg, setUnreadMsg] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [showCreateRec, setShowCreateRec] = useState(false);
  const recSel = useSelection();
  const appSel = useSelection();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [meR, sR, rR, aR, dR] = await Promise.all([
        api.get('/admin/me'),
        api.get('/admin/stats'),
        api.get('/admin/recruiters'),
        api.get('/admin/applications'),
        api.get('/admin/docusign/status').catch(() => ({ data: { ready: false, error: 'unknown' } })),
      ]);
      setMe(meR.data); setStats(sR.data); setRecs(rR.data); setApps(aR.data); setDsStatus(dR.data);
    } catch (e) { /* 401 handled globally */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Auto-refresh recruiters every 60s so "Online" presence dots stay fresh.
  useEffect(() => {
    const id = setInterval(() => { if (tab === 'recruiters') load(); }, 60000);
    return () => clearInterval(id);
  }, [tab, load]);

  // Poll unread message count every 20s
  useEffect(() => {
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get('/admin/messages/unread-count');
        if (!cancelled) setUnreadMsg(data?.unread || 0);
      } catch {}
    };
    fetchUnread();
    const iv = setInterval(fetchUnread, 20000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [tab]);

  const logout = () => { setToken(null); setRole(null); navigate('/admin-login'); };

  const matches = (o, q) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return Object.values(o).some((v) => typeof v === 'string' && v.toLowerCase().includes(s));
  };

  // --- Recruiter actions ---
  const saveCommission = async (rec) => {
    const val = parseFloat(commDraft);
    if (isNaN(val) || val < 0) { toast({ title: 'Invalid amount' }); return; }
    try {
      await api.patch(`/admin/recruiters/${rec.id}/commission`, { commission_per_student: val });
      toast({ title: 'Commission updated', description: `$${val.toLocaleString()} per student for ${rec.name}` });
      setEditingCommId(null); await load();
    } catch (e) { toast({ title: 'Update failed', description: e?.response?.data?.detail || 'Error' }); }
  };

  const activateRec = async (rec) => {
    const isResend = rec.status === 'active';
    const confirmMsg = isResend
      ? `Send a password reset link to ${rec.email}?`
      : `Activate ${rec.name}? An activation link will be emailed to ${rec.email} so they can set their own password.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const { data } = await api.post(`/admin/recruiters/${rec.id}/activate`);
      const titles = { activation_link: 'Activation link sent', reset_link: 'Reset link sent', credentials: 'Recruiter activated' };
      const descs = {
        activation_link: `${data.email_sent_to} will receive a link to set their password.`,
        reset_link: `Password reset link sent to ${data.email_sent_to}.`,
        credentials: `Login credentials sent to ${data.email_sent_to}.`,
      };
      toast({ title: titles[data.mode] || 'Email sent', description: descs[data.mode] || data.email_sent_to });
      await load();
    } catch (e) { toast({ title: 'Request failed', description: e?.response?.data?.detail || 'Error' }); }
  };

  const markSigned = async (rec) => {
    if (!window.confirm(`Manually mark ${rec.name}'s agreement as signed? (Use only if DocuSign webhook didn't fire.)`)) return;
    try {
      await api.post(`/admin/recruiters/${rec.id}/mark-signed`);
      toast({ title: 'Marked as signed' });
      await load();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || 'Error' }); }
  };

  const deleteRec = async (rec) => {
    if (!window.confirm(`Move ${rec.name} to Trash?`)) return;
    try {
      await api.delete(`/admin/recruiters/${rec.id}`);
      toast({ title: 'Moved to Trash', description: `${rec.name} can be restored from the Trash tab.` });
      await load();
    } catch (e) { toast({ title: 'Failed' }); }
  };

  const bulkDeleteRec = async () => {
    const ids = Array.from(recSel.selected);
    if (!ids.length) return;
    if (!window.confirm(`Move ${ids.length} recruiter${ids.length === 1 ? '' : 's'} to Trash?`)) return;
    try {
      const { data } = await api.post('/admin/recruiters/bulk-delete', { ids });
      toast({ title: 'Moved to Trash', description: `${data.trashed} item(s) in the Trash tab.` });
      recSel.clear();
      await load();
    } catch (e) { toast({ title: 'Bulk delete failed', description: e?.response?.data?.detail || 'Error' }); }
  };

  const deleteApp = async (a) => {
    if (!window.confirm(`Move ${a.name}'s application to Trash?`)) return;
    try {
      await api.delete(`/admin/applications/${a.id}`);
      toast({ title: 'Moved to Trash' });
      await load();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || 'Error' }); }
  };

  const bulkDeleteApp = async () => {
    const ids = Array.from(appSel.selected);
    if (!ids.length) return;
    if (!window.confirm(`Move ${ids.length} direct lead${ids.length === 1 ? '' : 's'} to Trash?`)) return;
    try {
      const { data } = await api.post('/admin/applications/bulk-delete', { ids });
      toast({ title: 'Moved to Trash', description: `${data.trashed} item(s) in the Trash tab.` });
      appSel.clear();
      await load();
    } catch (e) { toast({ title: 'Bulk delete failed', description: e?.response?.data?.detail || 'Error' }); }
  };

  const syncAgreementStatuses = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/admin/recruiters/refresh-agreement-statuses');
      const updated = data?.updated || 0;
      const checked = data?.checked || 0;
      toast({ title: updated > 0 ? `${updated} status${updated === 1 ? '' : 'es'} updated` : 'All agreements already up to date', description: `Checked ${checked} pending envelope${checked === 1 ? '' : 's'} with DocuSign.` });
      await load();
    } catch (e) {
      toast({ title: 'Sync failed', description: e?.response?.data?.detail || 'DocuSign unavailable. Check consent status.' });
    } finally { setSyncing(false); }
  };

  const refreshOneAgreement = async (rec) => {
    try {
      const { data } = await api.post(`/admin/recruiters/${rec.id}/refresh-agreement-status`);
      if (data.action === 'updated') {
        toast({ title: `Status updated → ${data.new_status.replace('_', ' ')}`, description: `${rec.name} — DocuSign status: ${data.docusign_status}` });
      } else if (data.action === 'consent_required') {
        toast({ title: 'DocuSign consent required', description: 'Grant consent from the banner at the top.' });
      } else if (data.action === 'no_envelope') {
        toast({ title: 'No DocuSign envelope', description: 'This agreement was sent via email fallback — use "Mark Signed" manually.' });
      } else {
        toast({ title: 'No change', description: `DocuSign reports: ${data.docusign_status || 'unknown'}` });
      }
      await load();
    } catch (e) { toast({ title: 'Check failed', description: e?.response?.data?.detail || 'Error' }); }
  };

  const updateAppStatus = async (id, status) => {
    try {
      await api.patch(`/admin/applications/${id}`, { status });
      toast({ title: `Application ${status}` });
      await load();
      if (selectedApp?.id === id) setSelectedApp({ ...selectedApp, status });
    } catch (e) { toast({ title: 'Update failed' }); }
  };

  const downloadDoc = async (appId, idx, name) => {
    try {
      const r = await api.get(`/admin/applications/${appId}/documents/${idx}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = name || 'document'; document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { toast({ title: 'Download failed' }); }
  };

  // --- Render ---
  const recFiltered = recs.filter((r) => matches(r, filter));
  const appFiltered = apps.filter((a) => matches(a, filter));

  const actionsForRecruiter = (r) => {
    if (r.status === 'pending') return { primary: 'agreement', label: 'Agreement', icon: MailOpen };
    if (r.status === 'agreement_sent') return { primary: 'mark_signed', label: 'Mark Signed', icon: CheckCircle2 };
    if (r.status === 'agreement_signed') return { primary: 'activate', label: 'Activate', icon: KeyRound };
    if (r.status === 'active') return { primary: 'resend_login', label: 'Send Login', icon: KeyRound };
    return { primary: 'agreement', label: 'Agreement', icon: MailOpen };
  };

  return (
    <main className="min-h-screen bg-[#f6faf3] flex">
      {/* Mobile nav drawer (< lg) */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" data-testid="admin-mobile-nav">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileNavOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-white border-r border-[#0B5E2A]/10 flex flex-col shadow-xl">
            <div className="flex items-center gap-3 px-5 h-[72px] border-b border-[#0B5E2A]/10">
              <img src={BRAND.logo} alt="HCC" className="h-10 w-10 rounded-full" />
              <div className="leading-tight flex-1">
                <div className="font-display text-[15px] font-bold brand-green">HCC Admin</div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-brand-gold font-semibold">Internal Console</div>
              </div>
              <button onClick={() => setMobileNavOpen(false)} data-testid="admin-mobile-nav-close" className="h-8 w-8 rounded-md border border-[#0B5E2A]/20 text-[#0B5E2A] flex items-center justify-center">
                <CloseIcon className="h-4 w-4" />
              </button>
            </div>
            <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
              {NAV.map((n) => {
                const Icon = n.icon; const active = tab === n.key;
                const showBadge = n.key === 'messages' && unreadMsg > 0;
                return (
                  <button key={n.key} data-testid={`admin-mobile-nav-${n.key}`} onClick={() => { setTab(n.key); setMobileNavOpen(false); }} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${active ? 'bg-[#0B5E2A] text-white' : 'text-[#4b5e4d] hover:bg-[#f0f6ee] hover:text-[#0B5E2A]'}`}>
                    <Icon className="h-4 w-4" /> <span className="flex-1 text-left">{n.label}</span>
                    {showBadge && (<span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full text-[11px] font-bold bg-[#F0B93C] text-[#06401d]">{unreadMsg}</span>)}
                  </button>
                );
              })}
            </nav>
            <div className="p-3 border-t border-[#0B5E2A]/10">
              <div className="text-xs font-semibold text-[#1b2a1f]">{me?.name || 'Admin'}</div>
              <div className="text-[11px] text-[#6b7e6d]">{me?.email}</div>
              <button onClick={() => { setShowChangePw(true); setMobileNavOpen(false); }} className="mt-2 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-[#0B5E2A]/20 text-sm text-[#0B5E2A] hover:bg-[#f0f6ee] transition-colors">
                <KeyRound className="h-4 w-4" /> Change Password
              </button>
              <button onClick={logout} className="mt-2 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-[#0B5E2A]/20 text-sm text-[#0B5E2A] hover:bg-[#f0f6ee] transition-colors">
                <LogOut className="h-4 w-4" /> Logout
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-[#0B5E2A]/10 h-screen sticky top-0">
        <Link to="/" className="flex items-center gap-3 px-5 h-[72px] border-b border-[#0B5E2A]/10">
          <img src={BRAND.logo} alt="HCC" className="h-10 w-10 rounded-full" />
          <div className="leading-tight">
            <div className="font-display text-[15px] font-bold brand-green">HCC Admin</div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-brand-gold font-semibold">Internal Console</div>
          </div>
        </Link>
        <nav className="flex-1 p-3 space-y-0.5">
          {NAV.map((n) => {
            const Icon = n.icon; const active = tab === n.key;
            const showBadge = n.key === 'messages' && unreadMsg > 0;
            return (
              <button key={n.key} data-testid={`admin-nav-${n.key}`} onClick={() => setTab(n.key)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${active ? 'bg-[#0B5E2A] text-white' : 'text-[#4b5e4d] hover:bg-[#f0f6ee] hover:text-[#0B5E2A]'}`}>
                <Icon className="h-4 w-4" /> <span className="flex-1 text-left">{n.label}</span>
                {showBadge && (<span className={`inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full text-[11px] font-bold ${active ? 'bg-[#F0B93C] text-[#06401d]' : 'bg-[#F0B93C] text-[#06401d]'}`}>{unreadMsg}</span>)}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-[#0B5E2A]/10">
          <div className="text-xs font-semibold text-[#1b2a1f]">{me?.name || 'Admin'}</div>
          <div className="text-[11px] text-[#6b7e6d]">{me?.email}</div>
          <button onClick={() => setShowChangePw(true)} data-testid="admin-change-password-btn" className="mt-2 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-[#0B5E2A]/20 text-sm text-[#0B5E2A] hover:bg-[#f0f6ee] transition-colors">
            <KeyRound className="h-4 w-4" /> Change Password
          </button>
          <button onClick={logout} data-testid="admin-logout-btn" className="mt-2 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-[#0B5E2A]/20 text-sm text-[#0B5E2A] hover:bg-[#f0f6ee] transition-colors">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <section className="flex-1 min-w-0">
        <header className="bg-white border-b border-[#0B5E2A]/10 sticky top-0 z-30">
          <div className="px-5 md:px-8 h-[72px] flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <button onClick={() => setMobileNavOpen(true)} data-testid="admin-mobile-nav-open" className="lg:hidden h-9 w-9 rounded-md border border-[#0B5E2A]/20 text-[#0B5E2A] hover:bg-[#f0f6ee] flex items-center justify-center shrink-0" aria-label="Open menu">
                <Menu className="h-4 w-4" />
              </button>
              <div className="min-w-0">
                <div className="font-display text-xl text-[#0B5E2A] capitalize truncate">{NAV.find((n) => n.key === tab)?.label}</div>
                <div className="text-xs text-[#6b7e6d] hidden sm:block">Manage recruiters, students, commissions, and platform activity</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {tab === 'recruiters' && (
                <>
                  <button onClick={() => setShowCreateRec(true)} data-testid="add-recruiter-btn" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#0B5E2A] text-white hover:bg-[#094a22] text-xs font-semibold transition-colors">
                    <Plus className="h-3.5 w-3.5" /> Add Recruiter
                  </button>
                  <button onClick={syncAgreementStatuses} disabled={syncing} data-testid="sync-docusign-btn" title="Poll DocuSign for signing updates" className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#0B5E2A]/20 text-[#0B5E2A] hover:bg-[#f0f6ee] text-xs font-semibold disabled:opacity-60">
                    <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Syncing...' : 'Sync DocuSign'}
                  </button>
                </>
              )}
              <div className="relative hidden sm:block">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6b7e6d]" />
                <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search..." className="pl-9 pr-3 py-2 w-56 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30" />
              </div>
              <button onClick={load} className="h-9 w-9 rounded-md border border-[#0B5E2A]/20 text-[#0B5E2A] hover:bg-[#f0f6ee] flex items-center justify-center transition-colors" title="Refresh">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </header>

        <div className="p-5 md:p-8">
          {/* DocuSign status banner */}
          {dsStatus && !dsStatus.ready && (
            <div className="mb-5 rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3">
              <ShieldAlert className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <div className="font-semibold text-amber-900">DocuSign consent required</div>
                <div className="text-amber-800 mt-1">
                  Before you can send agreements via DocuSign, an admin must grant consent once.
                  If DocuSign is unavailable, agreements will be emailed directly via Resend as a fallback.
                </div>
                {dsStatus.consent_url && (
                  <a href={dsStatus.consent_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-amber-900 font-semibold underline">
                    Grant DocuSign consent →
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Stat cards */}
          {(tab === 'recruiters' || tab === 'students' || tab === 'colleges') && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <StatCard icon={Users} label="Recruiters" value={stats?.recruiters?.total} accent="green" />
              <StatCard icon={Clock} label="Pending" value={stats?.recruiters?.pending} accent="gold" />
              <StatCard icon={FileText} label="Students" value={stats?.students?.total} accent="blue" />
              <StatCard icon={Building2} label="Colleges" value={stats?.colleges?.total} accent="purple" />
              <StatCard icon={DollarSign} label="Commissions" value={`$${Math.round(stats?.commissions?.total || 0).toLocaleString()}`} accent="rose" />
            </div>
          )}

          {/* (Revenue Forecast now lives inside the Commissions tab.) */}

          {/* --- RECRUITERS TAB --- */}
          {tab === 'recruiters' && (
            <>
              <BulkActionBar
                count={recSel.count}
                onClear={recSel.clear}
                onDelete={bulkDeleteRec}
                itemType="recruiter"
                testid="bulk-bar-recruiters"
              />
              <div className="bg-white border border-[#0B5E2A]/10 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" style={{ minWidth: 1150 }}>
                  <thead className="bg-[#f0f6ee] text-[#0B5E2A]">
                    <tr>
                      <Th className="w-10 text-center">
                        <SelectionCheckbox
                          checked={recFiltered.length > 0 && recFiltered.every((r) => recSel.has(r.id))}
                          onChange={() => recSel.toggleAll(recFiltered.map((r) => r.id))}
                          testid="rec-select-all"
                          label="Select all recruiters"
                        />
                      </Th>
                      <Th className="min-w-[200px]">Recruiter</Th>
                      <Th className="min-w-[180px]">Company</Th>
                      <Th className="min-w-[110px]">Country</Th>
                      <Th className="min-w-[90px] text-center">Students</Th>
                      <Th className="min-w-[170px] whitespace-nowrap">Commission / Student</Th>
                      <Th className="min-w-[140px]">Status</Th>
                      <Th className="min-w-[100px]">Date</Th>
                      <Th className="text-right pr-6 min-w-[240px]">Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {recFiltered.length === 0 && (
                      <tr><td colSpan={9} className="text-center py-10 text-[#6b7e6d]">No recruiters yet.</td></tr>
                    )}
                    {recFiltered.map((r) => {
                      const act = actionsForRecruiter(r);
                      const editing = editingCommId === r.id;
                      return (
                        <tr key={r.id} className={`border-t border-[#0B5E2A]/10 hover:bg-[#faf8ee] align-middle ${recSel.has(r.id) ? 'bg-[#f0f6ee]' : ''}`}>
                          <Td className="align-middle text-center">
                            <SelectionCheckbox
                              checked={recSel.has(r.id)}
                              onChange={() => recSel.toggle(r.id)}
                              testid={`rec-select-${r.id}`}
                              label={`Select ${r.name}`}
                            />
                          </Td>
                          <Td className="align-middle">
                            <div className="flex items-center gap-2">
                              <PresenceDot lastSeenAt={r.last_seen_at} />
                              <div className="min-w-0">
                                <div className="font-semibold text-[#0B5E2A] truncate max-w-[220px]" title={r.name}>{r.name}</div>
                                <div className="text-xs text-[#6b7e6d] truncate max-w-[220px]" title={r.email}>{r.email}</div>
                                <div className="text-[10px] text-[#6b7e6d] mt-0.5" data-testid={`rec-last-seen-${r.id}`}>{formatLastSeen(r.last_seen_at)}</div>
                              </div>
                            </div>
                          </Td>
                          <Td className="align-middle">
                            <div className="truncate max-w-[200px]" title={r.company}>{r.company}</div>
                            {(r.signup_documents || []).length > 0 && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {r.signup_documents.map((d, i) => (
                                  <a
                                    key={i}
                                    href="#"
                                    onClick={async (e) => { e.preventDefault(); try { const res = await api.get(`/admin/recruiters/${r.id}/signup-documents/${i}`, { responseType: 'blob' }); const url = URL.createObjectURL(res.data); const a = document.createElement('a'); a.href = url; a.download = d.name || 'document'; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url); } catch { toast({ title: 'Download failed' }); } }}
                                    data-testid={`recruiter-signup-doc-${r.id}-${i}`}
                                    className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#1e40af] hover:underline bg-[#DEE9FA] rounded px-1.5 py-0.5"
                                    title={d.name}
                                  >
                                    <FileText className="h-2.5 w-2.5" />
                                    {d.doc_type === 'id_document' ? 'ID' : 'License'}
                                  </a>
                                ))}
                              </div>
                            )}
                          </Td>
                          <Td className="align-middle whitespace-nowrap">{r.country}</Td>
                          <Td className="align-middle text-center"><span className="inline-flex h-7 min-w-[28px] px-2 items-center justify-center rounded-full bg-[#DEE9FA] text-[#1e40af] text-xs font-bold">{r.students_count || 0}</span></Td>
                          <Td className="align-middle whitespace-nowrap">
                            {editing ? (
                              <div className="flex items-center gap-1">
                                <span className="text-[#6b7e6d]">$</span>
                                <input autoFocus type="number" min="0" step="50" value={commDraft} onChange={(e) => setCommDraft(e.target.value)} className="w-24 px-2 py-1 rounded border border-[#0B5E2A]/25 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30" />
                                <button onClick={() => saveCommission(r)} className="h-7 w-7 rounded bg-[#E1F3E4] text-[#0B5E2A] flex items-center justify-center hover:bg-[#c9e7ce] transition-colors" title="Save"><Check className="h-4 w-4" /></button>
                                <button onClick={() => setEditingCommId(null)} className="h-7 w-7 rounded bg-[#FDE2E2] text-[#9b1c1c] flex items-center justify-center hover:bg-[#fbcfcf] transition-colors" title="Cancel"><CloseIcon className="h-4 w-4" /></button>
                              </div>
                            ) : (
                              <button onClick={() => { setEditingCommId(r.id); setCommDraft(String(r.commission_per_student || '')); }} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0B5E2A] hover:underline">
                                {r.commission_per_student ? `$${Number(r.commission_per_student).toLocaleString()}` : <span className="text-[#6b7e6d] italic font-normal">— Set</span>}
                                <Edit3 className="h-3.5 w-3.5 opacity-60" />
                              </button>
                            )}
                          </Td>
                          <Td className="align-middle"><StatusPill value={r.status} /></Td>
                          <Td className="align-middle text-xs text-[#6b7e6d] whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</Td>
                          <Td className="align-middle text-right pr-4">
                            <div className="flex items-center justify-end gap-1 whitespace-nowrap">
                              {act.primary === 'agreement' && (
                                <button onClick={() => setAgreeRec(r)} data-testid={`send-agreement-btn-${r.id}`} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded bg-[#0B5E2A] text-white hover:bg-[#094a22] transition-colors"><MailOpen className="h-3.5 w-3.5" /> Agreement</button>
                              )}
                              {act.primary === 'mark_signed' && (
                                <button onClick={() => markSigned(r)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded bg-[#DEE9FA] text-[#1e40af] hover:bg-[#c8d8f4] transition-colors"><CheckCircle2 className="h-3.5 w-3.5" /> Mark Signed</button>
                              )}
                              {/* Refresh from DocuSign — only for agreement_sent recruiters with an envelope */}
                              {r.status === 'agreement_sent' && r.docusign_envelope_id && (
                                <button onClick={() => refreshOneAgreement(r)} data-testid={`refresh-agreement-btn-${r.id}`} title="Check DocuSign for latest status" className="h-7 w-7 rounded text-[#0B5E2A] hover:bg-[#f0f6ee] border border-[#0B5E2A]/25 flex items-center justify-center transition-colors">
                                  <RefreshCw className="h-3.5 w-3.5" />
                                </button>
                              )}
                              {/* Resend Agreement — in case wrong PDF was uploaded */}
                              {(r.status === 'agreement_sent' || r.status === 'agreement_signed') && (
                                <button onClick={() => setAgreeRec({ ...r, _resend: true })} data-testid={`resend-agreement-btn-${r.id}`} title="Resend agreement with a different PDF" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded bg-white border border-[#0B5E2A]/25 text-[#0B5E2A] hover:bg-[#f0f6ee] transition-colors">
                                  <RotateCcw className="h-3.5 w-3.5" /> Resend
                                </button>
                              )}
                              {act.primary === 'activate' && (
                                <button onClick={() => activateRec(r)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded bg-[#F0B93C] text-[#06401d] hover:bg-[#e7a91f] transition-colors"><KeyRound className="h-3.5 w-3.5" /> Activate</button>
                              )}
                              {act.primary === 'resend_login' && (
                                <button onClick={() => activateRec(r)} data-testid="resend-login-btn" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded bg-[#F0B93C] text-[#06401d] hover:bg-[#e7a91f] transition-colors" title="Send a password reset link (does not rotate the current password)"><KeyRound className="h-3.5 w-3.5" /> Send Reset Link</button>
                              )}
                              <button onClick={() => deleteRec(r)} className="h-7 w-7 rounded text-[#9b1c1c] hover:bg-[#FDE2E2] flex items-center justify-center transition-colors" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                            </div>
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </div>
            </>
          )}

          {/* --- STUDENTS TAB (recruiter-submitted) --- */}
          {tab === 'students' && (
            <StudentsTab filter={filter} />
          )}

          {/* --- DIRECT LEADS (public /apply form) --- */}
          {tab === 'direct' && (
            <>
              <BulkActionBar count={appSel.count} onClear={appSel.clear} onDelete={bulkDeleteApp} itemType="direct lead" testid="bulk-bar-applications" />
              <div className="bg-white border border-[#0B5E2A]/10 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#f0f6ee] text-[#0B5E2A]">
                      <tr>
                        <Th className="w-10 text-center">
                          <SelectionCheckbox
                            checked={appFiltered.length > 0 && appFiltered.every((a) => appSel.has(a.id))}
                            onChange={() => appSel.toggleAll(appFiltered.map((a) => a.id))}
                            testid="app-select-all"
                            label="Select all direct leads"
                          />
                        </Th>
                        <Th>Applicant</Th><Th>Contact</Th><Th>Nationality</Th><Th>Status in CA</Th><Th>Docs</Th><Th>Status</Th><Th>Submitted</Th><Th className="text-right pr-6">Actions</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {appFiltered.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-[#6b7e6d]">No direct applications yet.</td></tr>}
                      {appFiltered.map((a) => (
                        <tr key={a.id} className={`border-t border-[#0B5E2A]/10 hover:bg-[#faf8ee] cursor-pointer ${appSel.has(a.id) ? 'bg-[#f0f6ee]' : ''}`} onClick={() => setSelectedApp(a)}>
                          <Td className="text-center" onClick={(e) => e.stopPropagation()}>
                            <SelectionCheckbox
                              checked={appSel.has(a.id)}
                              onChange={() => appSel.toggle(a.id)}
                              testid={`app-select-${a.id}`}
                              label={`Select ${a.name}`}
                            />
                          </Td>
                          <Td><div className="font-semibold">{a.name}</div><div className="text-xs text-[#6b7e6d]">DOB {a.dob}</div></Td>
                          <Td><div>{a.email}</div><div className="text-xs text-[#6b7e6d]">{a.phone_code} {a.phone}</div></Td>
                          <Td>{a.nationality}</Td>
                          <Td>{a.status_in_canada}</Td>
                          <Td>{(a.documents || []).length}</Td>
                          <Td><StatusPill value={a.status} /></Td>
                          <Td className="text-xs text-[#6b7e6d]">{new Date(a.created_at).toLocaleDateString()}</Td>
                          <Td onClick={(e) => e.stopPropagation()} className="text-right pr-4">
                            <div className="flex items-center justify-end gap-1 whitespace-nowrap">
                              <button onClick={() => updateAppStatus(a.id, 'approved')} className="px-2 py-1 text-xs rounded bg-[#E1F3E4] text-[#0B5E2A] font-semibold hover:bg-[#c9e7ce]">Approve</button>
                              <button onClick={() => updateAppStatus(a.id, 'rejected')} className="px-2 py-1 text-xs rounded bg-[#FDE2E2] text-[#9b1c1c] font-semibold hover:bg-[#fbcfcf]">Reject</button>
                              <button onClick={() => deleteApp(a)} data-testid={`delete-application-${a.id}`} title="Move to Trash" className="h-7 w-7 rounded text-[#9b1c1c] hover:bg-[#FDE2E2] flex items-center justify-center transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                            </div>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* --- STUDENT AID MASTER RECORD TAB --- */}
          {tab === 'student-aid' && (
            <StudentAidTab />
          )}

          {/* --- DOMESTIC APPLICATIONS TAB --- */}
          {tab === 'domestic' && (
            <DomesticAppsTab filter={filter} />
          )}

          {/* --- COLLEGES TAB --- */}
          {tab === 'colleges' && (
            <CollegesTab filter={filter} refresh={loading ? 1 : 0} />
          )}

          {/* --- FORWARD HISTORY TAB --- */}
          {tab === 'forwards' && (
            <ForwardsTab filter={filter} />
          )}

          {/* --- MESSAGES TAB --- */}
          {tab === 'messages' && (
            <MessagesTab />
          )}

          {/* --- COMMISSIONS TAB --- */}
          {tab === 'commissions' && (
            <CommissionsTab filter={filter} />
          )}

          {/* --- TRASH TAB --- */}
          {tab === 'trash' && (
            <TrashTab />
          )}

          {/* Placeholder tabs */}
          {['contact'].includes(tab) && (
            <div className="bg-white border border-[#0B5E2A]/10 rounded-xl p-12 text-center">
              <LayoutDashboard className="h-10 w-10 mx-auto brand-green opacity-60" />
              <div className="mt-3 font-display text-xl text-[#0B5E2A]">{NAV.find((n) => n.key === tab)?.label}</div>
              <p className="mt-2 text-sm text-[#6b7e6d]">This section is coming in the next phase.</p>
            </div>
          )}
        </div>
      </section>

      {/* Modals */}
      {agreeRec && (
        <SendAgreementModal recruiter={agreeRec} dsStatus={dsStatus} onClose={() => setAgreeRec(null)} onSent={() => { setAgreeRec(null); load(); }} />
      )}
      {selectedApp && (
        <ApplicationDrawer app={selectedApp} onClose={() => setSelectedApp(null)} onUpdateStatus={updateAppStatus} onDownload={downloadDoc} />
      )}
      {showChangePw && (
        <ChangePasswordModal role="admin" onClose={() => setShowChangePw(false)} />
      )}
      {showCreateRec && (
        <CreateRecruiterModal onClose={() => setShowCreateRec(false)} onCreated={() => { setShowCreateRec(false); load(); }} />
      )}
    </main>
  );
};

const Th = ({ children, className = '' }) => <th className={`text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider ${className}`}>{children}</th>;
const Td = ({ children, className = '', onClick }) => <td onClick={onClick} className={`px-4 py-3 align-middle ${className}`}>{children}</td>;

// Activity / presence helpers for the Recruiters table.
const ONLINE_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

const formatLastSeen = (iso) => {
  if (!iso) return 'Never logged in';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'Never logged in';
  const diff = Date.now() - then;
  if (diff < ONLINE_WINDOW_MS) return 'Online now';
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `Last seen ${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Last seen ${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `Last seen ${days} day${days === 1 ? '' : 's'} ago`;
  return `Last seen ${new Date(iso).toLocaleDateString()}`;
};

const PresenceDot = ({ lastSeenAt }) => {
  if (!lastSeenAt) {
    return <span className="h-2.5 w-2.5 rounded-full bg-[#d1d5db] shrink-0" title="Never logged in" />;
  }
  const diff = Date.now() - new Date(lastSeenAt).getTime();
  if (diff < ONLINE_WINDOW_MS) return <span className="h-2.5 w-2.5 rounded-full bg-[#22c55e] shrink-0 ring-2 ring-[#22c55e]/30 animate-pulse" title="Online now" />;
  if (diff < 24 * 60 * 60 * 1000) return <span className="h-2.5 w-2.5 rounded-full bg-[#F0B93C] shrink-0" title="Active today" />;
  return <span className="h-2.5 w-2.5 rounded-full bg-[#d1d5db] shrink-0" title="Inactive" />;
};

export default AdminDashboard;
